import React, { useState, useEffect } from "react";
import { Upload, FileText, Download, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserDocument, DOCUMENT_TYPES, DocumentType } from "./types";
import { formatFileSize, getFileIcon } from "./utils";

interface DocumentsTabProps {
  userId: string;
  isAdmin: boolean;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ userId, isAdmin }) => {
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // États pour le formulaire d'upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>("attestation");
  const [description, setDescription] = useState("");

  useEffect(() => {
    fetchDocuments();
  }, [userId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("user_documents")
        .select(`
          *,
          uploader:profiles!user_documents_uploaded_by_fkey (
            name,
            email
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Le fichier est trop volumineux (max 10MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !isAdmin) return;

    try {
      setUploading(true);

      // Générer un nom de fichier unique
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

      // Upload du fichier vers Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("user-documents")
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Enregistrer les métadonnées en base
      const { error: dbError } = await supabase
        .from("user_documents")
        .insert({
          user_id: userId,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
          file_name: selectedFile.name,
          file_path: fileName,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
          document_type: documentType,
          description: description.trim() || null
        });

      if (dbError) throw dbError;

      toast.success("Document uploadé avec succès");
      setUploadOpen(false);
      setSelectedFile(null);
      setDescription("");
      setDocumentType("attestation");
      fetchDocuments();
    } catch (error) {
      console.error("Erreur lors de l'upload:", error);
      toast.error("Erreur lors de l'upload du document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: UserDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from("user-documents")
        .download(doc.file_path);

      if (error) throw error;

      // Créer un lien de téléchargement
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDelete = async (doc: UserDocument) => {
    if (!isAdmin) return;

    if (confirm(`Êtes-vous sûr de vouloir supprimer "${doc.file_name}" ?`)) {
      try {
        // Supprimer le fichier du storage
        const { error: storageError } = await supabase.storage
          .from("user-documents")
          .remove([doc.file_path]);

        if (storageError) throw storageError;

        // Supprimer l'enregistrement de la base
        const { error: dbError } = await supabase
          .from("user_documents")
          .delete()
          .eq("id", doc.id);

        if (dbError) throw dbError;

        toast.success("Document supprimé avec succès");
        fetchDocuments();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-[#BD1E28] animate-spin" />
        <span className="ml-2">Chargement des documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header avec bouton d'ajout pour les admins */}
      {isAdmin && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-[#333333]">Documents</h3>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#BD1E28] hover:bg-[#BD1E28]/90">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter un document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="file">Fichier</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formats acceptés: PDF, JPG, PNG, DOC, DOCX (max 10MB)
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="type">Type de document</Label>
                  <Select value={documentType} onValueChange={(value: DocumentType) => setDocumentType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOCUMENT_TYPES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Description (optionnel)</Label>
                  <Textarea
                    id="description"
                    placeholder="Description du document..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setUploadOpen(false)}>
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="bg-[#BD1E28] hover:bg-[#BD1E28]/90"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Upload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Uploader
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Liste des documents */}
      {documents.length === 0 ? (
        <Card className="p-6 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>Aucun document trouvé</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="text-[#BD1E28]">
                      {getFileIcon(doc.file_type)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-[#333333]">{doc.file_name}</h4>
                      <p className="text-sm text-[#666666]">
                        {DOCUMENT_TYPES[doc.document_type as DocumentType]} • {formatFileSize(doc.file_size)}
                      </p>
                      {doc.description && (
                        <p className="text-sm text-[#666666] mt-1">{doc.description}</p>
                      )}
                      <div className="text-xs text-[#888888] mt-1">
                        Ajouté le {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                        {doc.uploader && ` par ${doc.uploader.name}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(doc)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
